import React, { useState, useEffect } from 'react';
import { siteInfo } from '../constants/siteInfo';
import { isPublicHoliday, isPeakSummer } from '../utils/dateUtils';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

interface InfoBannerProps {
  onBannerClick: () => void;
}

const InfoBanner: React.FC<InfoBannerProps> = ({ onBannerClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const checkOpeningStatus = () => {
      const now = new Date();
      const currentDay = now.getDay();
      const currentHour = now.getHours();

      const holiday = isPublicHoliday(now);
      const peakSummer = isPeakSummer(now);
      
      let restaurantRanges = null;
      let etangsRanges = null;

      if (holiday || peakSummer) {
        restaurantRanges = siteInfo.openingHours.special.restaurant;
        etangsRanges = siteInfo.openingHours.special.etangs;
      } else {
        const todaySchedule = siteInfo.openingHours.standard.find(d => d.day === currentDay);
        restaurantRanges = todaySchedule?.services?.restaurant;
        etangsRanges = todaySchedule?.services?.etangs;
      }
      
      const restaurantOpenRange = restaurantRanges?.find(range => currentHour >= range.open && currentHour < range.close);
      const etangsOpenRange = etangsRanges?.find(range => currentHour >= range.open && currentHour < range.close);

      const isRestaurantOpen = !!restaurantOpenRange;
      const isEtangsOpen = !!etangsOpenRange;

      const overallOpen = isRestaurantOpen || isEtangsOpen;
      setIsOpen(overallOpen);

      if (isRestaurantOpen && isEtangsOpen) {
          setMessage(`Restaurant & Étangs ouverts`);
      } else if (isRestaurantOpen) {
          setMessage(`Restaurant ouvert (ferme à ${restaurantOpenRange.close}h)`);
      } else if (isEtangsOpen) {
          setMessage(`Étangs ouverts (ferment à ${etangsOpenRange.close}h)`);
      } else {
          // Si tout est fermé, trouver la prochaine ouverture
          const nextRestaurantOpening = restaurantRanges?.find(range => currentHour < range.open)?.open;
          const nextEtangsOpening = etangsRanges?.find(range => currentHour < range.open)?.open;

          const nextOpeningTime = Math.min(nextRestaurantOpening || Infinity, nextEtangsOpening || Infinity);

          if (nextOpeningTime !== Infinity) {
              let serviceName = 'Restaurant & Étangs';
              if (nextOpeningTime === nextRestaurantOpening && nextOpeningTime !== nextEtangsOpening) {
                  serviceName = 'Restaurant';
              } else if (nextOpeningTime !== nextRestaurantOpening && nextOpeningTime === nextEtangsOpening) {
                  serviceName = 'Étangs';
              }
              setMessage(`Fermé - Prochaine ouverture: ${serviceName} à ${nextOpeningTime}h`);
          } else {
              setMessage('Le Moulin est fermé aujourd\'hui');
          }
      }
    };

    checkOpeningStatus();
    const interval = setInterval(checkOpeningStatus, 60000); 
    return () => clearInterval(interval);
  }, []);

  return (
    <div 
      onClick={onBannerClick}
      className={`${isOpen ? 'bg-green-600' : 'bg-red-600'} text-white text-center p-2 text-sm shadow-md cursor-pointer hover:opacity-90 transition-opacity`}
    >
      <div className="container mx-auto flex items-center justify-center">
        {isOpen ? <FaCheckCircle className="mr-2" /> : <FaTimesCircle className="mr-2" />}
        <span>{message.trim()}</span>
        <span className="hidden sm:inline-block ml-2 text-xs opacity-75">(cliquez pour voir les détails)</span>
      </div>
    </div>
  );
};

export default InfoBanner; 